import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CustomersAPI } from "../api/customers";
import type {
  CreateCustomerRequest,
  Customer,
  UpdateCustomerRequest,
} from "../types";
import { useApiScope } from "@/features/utils/api-scope";
import { isDemoMode } from "@/features/utils/demo-mode";
import {
  mockCustomersResult,
  getMockCustomer,
} from "./use-customers.mock";

// Query Keys
export const customerKeys = {
  all: ["customers"] as const,
  lists: () => [...customerKeys.all, "list"] as const,
  list: (clientId: string, params?: { search?: string }) =>
    [...customerKeys.lists(), clientId, params] as const,
  details: () => [...customerKeys.all, "detail"] as const,
  detail: (clientId: string, customerId: string) =>
    [...customerKeys.details(), clientId, customerId] as const,
  // A list row's view of one customer (`use-customer-summary.ts`).
  summaries: () => [...customerKeys.all, "summary"] as const,
  summary: (clientId: string, customerId: string) =>
    [...customerKeys.summaries(), clientId, customerId] as const,
};

// List Customers Query
export function useCustomers(
  clientId: string,
  params?: {
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    hide_unknown?: boolean;
  },
) {
  // The client's file in the platform back-office reads through the staff mount;
  // the scope closes the key so both caches never mix.
  const scope = useApiScope();
  return useQuery<{ customers: Customer[]; total: number }>({
    queryKey: [...customerKeys.list(clientId, params), scope],
    queryFn: async () => {
      // Return mock data in demo mode
      if (isDemoMode()) {
        return mockCustomersResult;
      }

      const response = await CustomersAPI.listCustomers(
        clientId,
        params,
        scope,
      );
      return { customers: response.customers, total: response.total };
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!clientId,
  });
}

// Get Customer by ID Query
export function useCustomer(clientId: string, customerId: string) {
  const scope = useApiScope();
  return useQuery<{
    customer: Customer | null;
    callback_preferences: {
      next: number;
      rrule: string;
      text: string;
    };
  }>({
    queryKey: [...customerKeys.detail(clientId, customerId), scope],
    queryFn: async () => {
      // Return mock data in demo mode
      if (isDemoMode()) {
        return getMockCustomer(customerId);
      }

      const response = await CustomersAPI.getCustomer(
        clientId,
        customerId,
        scope,
      );
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!clientId && !!customerId,
  });
}

// Create Customer Mutation
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: CreateCustomerRequest;
    }) => CustomersAPI.createCustomer(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}

// Update Customer Mutation
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      customerId,
      data,
    }: {
      clientId: string;
      customerId: string;
      data: UpdateCustomerRequest;
    }) => CustomersAPI.updateCustomer(clientId, customerId, data),
    onSuccess: (_, { clientId, customerId }) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: customerKeys.detail(clientId, customerId),
      });
      queryClient.invalidateQueries({
        queryKey: customerKeys.summary(clientId, customerId),
      });
    },
  });
}

// Delete Customer Mutation
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      customerId,
    }: {
      clientId: string;
      customerId: string;
    }) => CustomersAPI.deleteCustomer(clientId, customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}
